import {
  isSupportedFontKey,
  type PdfFontKey,
} from "@/src/lib/pdf/fonts";
import type { PdfOptions } from "@/src/lib/pdf/print-document";
import {
  isEditorPreferencesSource,
  mergePdfOptionsWithDefaults,
  parsePartialPdfOptions,
  type EditorPreferencesSource,
} from "@/src/lib/storage/user-preferences";
import { getEmbeddedEditorDocument } from "./embedded-editor-document";
import { writeChatShareImportDraft } from "../utils/chat-share-import-draft";
import { writeRepoMarkdownImportDraft } from "../utils/repo-markdown-import-draft";
import type { ChatShareProvider } from "@/src/lib/chat-share/types";

export const EDITOR_HANDOFF_STORAGE_KEY = "markdown2pdf-editor-handoff";
export const EDITOR_HANDOFF_QUERY_PARAM = "handoff";
export const EDITOR_BLANK_QUERY_PARAM = "blank";
/** Keeps ChatGPT/Gemini/readme import UI on /markdown-editor after navigation. */
export const EDITOR_CHAT_SHARE_IMPORT_QUERY_PARAM = "import";

export type EditorHandoffSettings = Readonly<{
  pdfOptions: PdfOptions;
  editorFontFamily: PdfFontKey;
  editorFontSize: number;
}>;

export type EditorHandoffDocument = Readonly<{
  markdown: string;
  filename: string;
  /** Preferences namespace for the destination fullscreen editor. */
  source?: EditorPreferencesSource;
  /** PDF + editor font settings from the embedded editor (Maximize handoff). */
  settings?: EditorHandoffSettings;
}>;

export type MarkdownEditorUrlOptions = Readonly<{
  chatShareProvider?: "chatgpt" | "gemini";
  /** Show repository Markdown import toolbar (`?import=readme`). */
  repoMarkdownImport?: boolean;
}>;

type StoredHandoff = Readonly<{
  id: string;
  markdown: string;
  filename: string;
  source?: EditorPreferencesSource;
  pdfOptions?: Partial<PdfOptions>;
  editorFontFamily?: PdfFontKey;
  editorFontSize?: number;
}>;

/**
 * Replay buffer keyed by handoff id so React Strict Mode remounts can re-read
 * after sessionStorage was cleared. Cleared once the handoff query is gone.
 */
const replayById = new Map<string, EditorHandoffDocument>();

function isValidFontSize(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 8 &&
    value <= 24
  );
}

function parseHandoffSettings(
  stored: StoredHandoff,
): EditorHandoffSettings | undefined {
  // Settings are only present on Maximize handoffs. Missing editor fonts means
  // treat as document-only handoff (hero / open-file flows).
  if (
    stored.pdfOptions === undefined ||
    !isSupportedFontKey(stored.editorFontFamily) ||
    !isValidFontSize(stored.editorFontSize)
  ) {
    return undefined;
  }

  return {
    pdfOptions: mergePdfOptionsWithDefaults(
      parsePartialPdfOptions(stored.pdfOptions),
    ),
    editorFontFamily: stored.editorFontFamily,
    editorFontSize: stored.editorFontSize,
  };
}

function toHandoffDocument(stored: StoredHandoff): EditorHandoffDocument {
  const filename = stored.filename;
  const settings = parseHandoffSettings(stored);
  const source = isEditorPreferencesSource(stored.source)
    ? stored.source
    : undefined;
  return {
    markdown: stored.markdown,
    filename: typeof filename === "string" ? filename : "",
    ...(source ? { source } : {}),
    ...(settings ? { settings } : {}),
  };
}

/** Always drop the sessionStorage payload (safe no-op if missing). */
export function clearEditorHandoffStorage(): void {
  try {
    window.sessionStorage.removeItem(EDITOR_HANDOFF_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable.
  }
}

/**
 * Stash a document for a one-shot open of /markdown-editor?handoff=<id>.
 * Returns the handoff id, or null if storage is unavailable.
 */
export function stashEditorHandoff(
  document: EditorHandoffDocument,
): string | null {
  const id = crypto.randomUUID();
  try {
    const payload: StoredHandoff = {
      id,
      markdown: document.markdown,
      filename: document.filename,
      ...(document.source ? { source: document.source } : {}),
      ...(document.settings
        ? {
            pdfOptions: document.settings.pdfOptions,
            editorFontFamily: document.settings.editorFontFamily,
            editorFontSize: document.settings.editorFontSize,
          }
        : {}),
    };
    window.sessionStorage.setItem(
      EDITOR_HANDOFF_STORAGE_KEY,
      JSON.stringify(payload),
    );
    return id;
  } catch {
    return null;
  }
}

/**
 * Read a handoff only when `handoffId` matches the stashed id.
 * Without an id (normal editor visits), always returns null.
 * Clears sessionStorage as soon as a matching payload is consumed.
 */
export function takeEditorHandoff(
  handoffId: string | null | undefined,
): EditorHandoffDocument | null {
  if (!handoffId || typeof window === "undefined") {
    return null;
  }

  const replayed = replayById.get(handoffId);
  if (replayed) {
    return replayed;
  }

  try {
    const raw = window.sessionStorage.getItem(EDITOR_HANDOFF_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as { id?: unknown }).id !== "string" ||
      typeof (parsed as { markdown?: unknown }).markdown !== "string"
    ) {
      clearEditorHandoffStorage();
      return null;
    }

    const stored = parsed as StoredHandoff;
    // Consume the stored payload either way so it cannot linger after this visit.
    clearEditorHandoffStorage();

    if (stored.id !== handoffId) {
      return null;
    }

    const document = toHandoffDocument(stored);
    replayById.set(handoffId, document);
    return document;
  } catch {
    clearEditorHandoffStorage();
    return null;
  }
}

/** Drop replay once the handoff query has been consumed/cleaned. */
export function clearEditorHandoffReplay(handoffId?: string): void {
  if (handoffId) {
    replayById.delete(handoffId);
    return;
  }
  replayById.clear();
}

/** Reset handoff state (tests only). */
export function resetEditorHandoffCache(): void {
  replayById.clear();
}

function appendImportParam(
  params: URLSearchParams,
  options?: MarkdownEditorUrlOptions,
) {
  if (options?.repoMarkdownImport) {
    params.set(EDITOR_CHAT_SHARE_IMPORT_QUERY_PARAM, "readme");
    return;
  }
  if (options?.chatShareProvider) {
    params.set(
      EDITOR_CHAT_SHARE_IMPORT_QUERY_PARAM,
      options.chatShareProvider,
    );
  }
}

export function markdownEditorUrl(
  options?: MarkdownEditorUrlOptions,
): string {
  if (!options?.chatShareProvider && !options?.repoMarkdownImport) {
    return "/markdown-editor";
  }

  const params = new URLSearchParams();
  appendImportParam(params, options);
  return `/markdown-editor?${params.toString()}`;
}

export function markdownEditorHandoffUrl(
  handoffId: string,
  options?: MarkdownEditorUrlOptions,
): string {
  const params = new URLSearchParams({
    [EDITOR_HANDOFF_QUERY_PARAM]: handoffId,
  });
  appendImportParam(params, options);
  return `/markdown-editor?${params.toString()}`;
}

/** Open the editor empty — URL-driven so SSR and hydration stay in sync. */
export function markdownEditorBlankUrl(
  options?: MarkdownEditorUrlOptions,
): string {
  const params = new URLSearchParams({
    [EDITOR_BLANK_QUERY_PARAM]: "1",
  });
  appendImportParam(params, options);
  return `/markdown-editor?${params.toString()}`;
}

export type NavigateToFullscreenEditorOptions = Readonly<{
  fallbackMarkdown: string;
  fallbackFilename?: string;
  source?: EditorPreferencesSource;
  settings?: EditorHandoffSettings;
  chatShareProvider?: ChatShareProvider;
  chatShareImportUrl?: string;
  repoMarkdownImport?: boolean;
  repoMarkdownImportUrl?: string;
}>;

/**
 * Stash the current (or fallback) document + optional settings and navigate to
 * /markdown-editor. Shared by hero links and the embedded Maximize control.
 */
export function navigateToFullscreenEditor(
  options: NavigateToFullscreenEditorOptions,
): void {
  const handoffOptions: MarkdownEditorUrlOptions | undefined =
    options.repoMarkdownImport
      ? { repoMarkdownImport: true }
      : options.chatShareProvider
        ? { chatShareProvider: options.chatShareProvider }
        : undefined;
  const href = markdownEditorUrl(handoffOptions);

  if (options.repoMarkdownImport) {
    writeRepoMarkdownImportDraft(options.repoMarkdownImportUrl?.trim() ?? "");
  } else if (options.chatShareProvider) {
    writeChatShareImportDraft(
      options.chatShareProvider,
      options.chatShareImportUrl?.trim() ?? "",
    );
  }

  const embedded = getEmbeddedEditorDocument();
  const document: EditorHandoffDocument = {
    markdown: embedded?.markdown ?? options.fallbackMarkdown,
    filename: embedded?.filename ?? options.fallbackFilename ?? "",
    ...(options.source ? { source: options.source } : {}),
    ...(options.settings ? { settings: options.settings } : {}),
  };

  const handoffId = stashEditorHandoff(document);
  if (!handoffId) {
    window.location.assign(href);
    return;
  }
  window.location.assign(markdownEditorHandoffUrl(handoffId, handoffOptions));
}
